import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Phone, MessageCircle, Send, MapPin, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, PHONE, PHONE_DISPLAY, WHATSAPP_MSG, SERVICES } from "../../lib/data";
import Typewriter from "../Typewriter";

const MUMBAI =
  "https://images.pexels.com/photos/5414582/pexels-photo-5414582.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export const ContactSection = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Thanks! Your enquiry has landed. We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try WhatsApp or call us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact"
      className="relative overflow-hidden border-t border-white/10"
    >
      <div className="absolute inset-0">
        <img src={MUMBAI} alt="Mumbai skyline" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#000000]/90" />
      </div>

      <div className="relative z-10 mx-auto max-w-[90rem] px-6 py-28 md:px-12 md:py-40">
        <div className="grid gap-16 md:grid-cols-12">
          <div className="md:col-span-6">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-white">
              / Numbers over niceties
            </p>
            <h2 className="mt-4 font-display text-4xl font-extrabold leading-[0.95] tracking-tighter text-white md:text-7xl">
              Let’s talk
              <br />
              <span className="font-serif-emph font-normal">
                <Typewriter
                  words={["numbers.", "revenue.", "growth.", "returns."]}
                  typingSpeed={100}
                  erasingSpeed={55}
                  pause={1400}
                />
              </span>
            </h2>
            <p className="mt-6 max-w-md text-white/70 leading-relaxed">
              Message us on WhatsApp, ring the office, or drop your details below.
              A senior operator (not a chatbot) replies within one business day.
              We won’t send you a sales deck — we’ll send you two things to try
              this week.
            </p>

            <div className="mt-10 flex flex-col gap-4 sm:flex-row">
              <a
                href={`https://wa.me/${PHONE.replace("+", "")}?text=${WHATSAPP_MSG}`}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-whatsapp"
                className="group flex items-center gap-3 rounded-2xl bg-white px-6 py-4 font-semibold text-black transition-transform hover:scale-[1.02]"
              >
                <MessageCircle size={22} /> WhatsApp
              </a>
              <a
                href={`tel:${PHONE}`}
                data-testid="contact-call"
                className="group flex items-center gap-3 rounded-2xl border border-white/20 px-6 py-4 font-semibold text-white transition-colors hover:bg-white/5"
              >
                <Phone size={22} className="text-white" /> {PHONE_DISPLAY}
              </a>
            </div>

            <div className="mt-8 flex items-center gap-2 text-white/75">
              <MapPin size={18} className="text-white" /> Mumbai, Maharashtra, India
            </div>

            <Link
              to="/booking"
              data-testid="contact-book-link"
              className="mt-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-white hover:text-white transition-colors"
            >
              Or schedule an appointment <ArrowRight size={14} />
            </Link>
          </div>

          <div className="md:col-span-6">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="glass rounded-3xl p-8 md:p-10"
            >
              <div className="grid gap-5">
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Name" testid="contact-name">
                    <input
                      data-testid="contact-name-input"
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Your name"
                      className="input-dark"
                    />
                  </Field>
                  <Field label="Email" testid="contact-email">
                    <input
                      data-testid="contact-email-input"
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="you@company.com"
                      className="input-dark"
                    />
                  </Field>
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Phone (optional)" testid="contact-phone">
                    <input
                      data-testid="contact-phone-input"
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="+91 ..."
                      className="input-dark"
                    />
                  </Field>
                  <Field label="Service" testid="contact-service">
                    <select
                      data-testid="contact-service-input"
                      value={form.service}
                      onChange={update("service")}
                      className="input-dark"
                    >
                      <option value="">Select a service</option>
                      {SERVICES.map((s) => (
                        <option key={s.id} value={s.title}>
                          {s.title}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>
                <Field label="Message" testid="contact-message">
                  <textarea
                    data-testid="contact-message-input"
                    value={form.message}
                    onChange={update("message")}
                    rows={4}
                    placeholder="Tell us about your goals..."
                    className="input-dark resize-none"
                  />
                </Field>
                <motion.button
                  type="submit"
                  disabled={loading}
                  whileTap={{ scale: 0.97 }}
                  data-testid="contact-submit"
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-6 py-4 font-semibold text-black transition-colors hover:bg-white/90 disabled:opacity-60"
                >
                  {loading ? "Sending..." : "Send Enquiry"}
                  {!loading && <Send size={18} />}
                </motion.button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-2 block font-mono text-xs uppercase tracking-[0.15em] text-white/75">
      {label}
    </span>
    {children}
  </label>
);

export default ContactSection;
